import { t } from "@travily/i18n";
import type Message from "@travily/types/models/MessagesMessage";
import { MessageGroup } from "@ui/components/MessageGroup";
import { type MessageReaction, ReactionPicker, ReactionPills, ReactionTrigger } from "@ui/components/ReactionPicker";
import { ReadReceipt } from "@ui/components/ReadReceipt";
import { useCanHover } from "@ui/hooks/use-can-hover";
import { dayKey, formatDayLabel } from "@ui/lib/format-message-time";
import { useLongPressGesture } from "@ui/primitives/LongPress";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ConversationDetailSkeleton } from "@/components/ConversationDetailSkeleton/ConversationDetailSkeleton";
import { MessageAttachments } from "@/components/MessageAttachments/MessageAttachments";
import { PersonAvatar } from "@/components/PersonAvatar/PersonAvatar";

interface GroupedMessage {
  senderId: number;
  senderName: string | null;
  senderInitial: string | null;
  senderAvatar: Message["sender_avatar"];
  firstMessageId: number;
  messages: Array<{ id: number; text: string; timestamp: string }>;
}

/** Attachments by message id — the group items stay a flat text shape. */
type AttachmentsByMessage = Record<number, Message["attachments"]>;

interface MessageDay {
  key: string;
  label: string;
  groups: GroupedMessage[];
}

/** Buckets messages by calendar day, then into runs by consecutive sender within each day — a day
 *  boundary always starts a new run even when the same person spoke either side of it. */
function groupByDay(messages: Message[]): { days: MessageDay[]; attachments: AttachmentsByMessage } {
  const days: MessageDay[] = [];
  const attachments: AttachmentsByMessage = {};
  let currentDay: MessageDay | undefined;
  let currentGroup: GroupedMessage | undefined;

  for (const message of messages) {
    const key = dayKey(message.created_at);
    if (currentDay?.key !== key) {
      currentDay = { key, label: formatDayLabel(message.created_at), groups: [] };
      days.push(currentDay);
      currentGroup = undefined;
    }
    if (currentGroup?.senderId !== message.sender_id) {
      currentGroup = {
        senderId: message.sender_id,
        senderName: message.sender_name,
        senderInitial: message.sender_initial,
        senderAvatar: message.sender_avatar,
        firstMessageId: message.id,
        messages: [],
      };
      currentDay.groups.push(currentGroup);
    }
    currentGroup.messages.push({ id: message.id, text: message.body, timestamp: message.created_at });
    attachments[message.id] = message.attachments;
  }

  return { days, attachments };
}

/**
 * The id of the last own run the counterpart has already seen — every message in it predates their
 * read marker. Anchors on the run's *last* message because the receipt renders below the run: a run
 * that straddles the marker has messages they have not seen, and a receipt that over-claims is worse
 * than one that lags.
 */
function readRunId(days: MessageDay[], currentUserId: number, readAt: string | null): number | undefined {
  if (!readAt) return undefined;
  const readTime = Date.parse(readAt);
  let id: number | undefined;

  for (const day of days) {
    for (const group of day.groups) {
      const last = group.messages.at(-1);
      if (group.senderId !== currentUserId || !last) continue;
      if (Date.parse(last.timestamp) <= readTime) id = group.firstMessageId;
    }
  }

  return id;
}

/** Long press is the touch route to the picker; the hover trigger is the pointer route. Where hover
 *  works the context stays null — an armed gesture would suppress bubble text selection for no gain.
 *  Read through a context so the row component can live at module scope, where its hook belongs. */
const LongPressContext = createContext<{ label: string; onLongPress: (messageId: number) => void } | null>(null);

function MessageRow({ children, message }: { children: ReactNode; message: { id: number } }) {
  const press = useContext(LongPressContext);
  const pressProps = useLongPressGesture({
    accessibilityDescription: press?.label,
    // An optimistic message has no server row to react to yet.
    isDisabled: message.id < 0 || !press,
    onLongPress: () => press?.onLongPress(message.id),
  });

  return <div {...pressProps}>{children}</div>;
}

// Slack for "still at the bottom": a reader within about a bubble of the end counts as parked there.
const PINNED_SLACK = 64;

interface MessageThreadProps {
  attachments: AttachmentsByMessage;
  currentUserId: number;
  days: MessageDay[];
  onToggleReaction: (messageId: number, emoji: string) => void;
  reactions: Record<string, MessageReaction[]>;
  receipt: string;
  receiptRunId: number | undefined;
}

/**
 * The rendered thread. Split from `MessageList` so its hooks sit above no early return — the
 * skeleton and empty branches live in the parent.
 */
function MessageThread({
  attachments,
  currentUserId,
  days,
  onToggleReaction,
  reactions,
  receipt,
  receiptRunId,
}: MessageThreadProps) {
  // One picker for the whole thread: only one can be open, and a per-bubble instance would mount a
  // Silk sheet per message.
  const [activeMessageId, setActiveMessageId] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const canHover = useCanHover();
  const pickerLabel = t("components.views.shared.message_list.add_reaction");

  const press = useMemo(
    () => (canHover ? null : { label: pickerLabel, onLongPress: setActiveMessageId }),
    [canHover, pickerLabel],
  );

  return (
    <LongPressContext value={press}>
      <ReactionPicker
        label={pickerLabel}
        onOpenChange={(open) => !open && setActiveMessageId(null)}
        onSelect={(emoji) => activeMessageId !== null && onToggleReaction(activeMessageId, emoji)}
        isOpen={activeMessageId !== null}
        triggerRef={triggerRef}
      />
      {days.map((day) => (
        <div key={day.key} className="flex flex-col gap-4">
          <p className="text-center font-semibold text-neutral-500 text-xs">{day.label}</p>
          {day.groups.map((group) => (
            <MessageGroup
              key={group.firstMessageId}
              footer={group.firstMessageId === receiptRunId ? <ReadReceipt>{receipt}</ReadReceipt> : undefined}
              isOwn={group.senderId === currentUserId}
              MessageRow={MessageRow}
              messages={group.messages}
              renderMessageActions={(message) =>
                message.id < 0 ? null : (
                  <ReactionTrigger
                    ref={activeMessageId === message.id ? triggerRef : undefined}
                    label={pickerLabel}
                    onPress={() => setActiveMessageId(message.id)}
                  />
                )
              }
              renderMessageMedia={(message) => <MessageAttachments attachments={attachments[message.id] ?? []} />}
              renderMessageFooter={(message) => (
                <ReactionPills
                  labelFor={(reaction) =>
                    t("components.views.shared.message_list.reaction_count", {
                      count: reaction.count,
                      emoji: reaction.emoji,
                    })
                  }
                  onToggle={(emoji) => onToggleReaction(message.id, emoji)}
                  reactions={reactions[String(message.id)] ?? []}
                />
              )}
              sender={{
                avatar: (
                  <PersonAvatar
                    person={{ avatar: group.senderAvatar, initial: group.senderInitial }}
                    shape="circle"
                    size="32"
                  />
                ),
                name: group.senderName ?? "",
              }}
            />
          ))}
        </div>
      ))}
    </LongPressContext>
  );
}

interface MessageListProps {
  currentUserId: number;
  messages: Message[] | undefined;
  onToggleReaction: (messageId: number, emoji: string) => void;
  /** When the other side last opened the thread. Null until they have. */
  readAt: string | null;
  /** Names for the receipt copy. Empty when the counterpart has no display name to show. */
  readers: string[];
  reactions: Record<string, MessageReaction[]>;
  /** The counterpart's typing bubble, rendered where the message it stands in for will land. */
  typingIndicator?: ReactNode;
}

export function MessageList({
  currentUserId,
  messages,
  onToggleReaction,
  readAt,
  readers,
  reactions,
  typingIndicator,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number | undefined>(undefined);
  const isFirstScrollRef = useRef(true);
  // Content grows under the reader after the fact — an image finishes loading, a reaction pill
  // appears — and the newest message slides behind the composer. One observation on the content
  // wrapper sees every such growth, including day buckets added after mount, and re-pins only a
  // reader who was already at the bottom; the container observation catches the viewport itself
  // shrinking. A callback ref rather than a mount effect: on a fresh thread the skeleton or empty
  // branch renders first, and the observer must attach whenever the container finally exists.
  const attachBottomPin = useCallback((el: HTMLDivElement) => {
    containerRef.current = el;
    let wasPinned = true;
    const observer = new ResizeObserver(() => {
      if (wasPinned) el.scrollTo({ top: el.scrollHeight });
    });
    const onScroll = () => {
      wasPinned = el.scrollHeight - el.scrollTop - el.clientHeight < PINNED_SLACK;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    observer.observe(el, { box: "content-box" });
    if (contentRef.current) observer.observe(contentRef.current);
    return () => {
      containerRef.current = null;
      observer.disconnect();
      el.removeEventListener("scroll", onScroll);
    };
  }, []);
  // Pin the view to the newest message on load and whenever the newest message
  // changes (a sent message or an inbound realtime reload). Without this the
  // overflow container stays scrolled to the top and new messages land below the
  // fold. Reading the last id here both drives the scroll and keeps `messages` a
  // genuinely-used dependency; the ref guard avoids re-scrolling on reloads that
  // don't change the newest message (e.g. a participations-only refresh).
  useEffect(() => {
    const el = containerRef.current;
    const lastId = messages && messages.length > 0 ? messages.at(-1)?.id : undefined;
    if (!el || lastId === undefined || lastId === lastMessageIdRef.current) return;
    lastMessageIdRef.current = lastId;
    el.scrollTo({ top: el.scrollHeight, behavior: isFirstScrollRef.current ? "auto" : "smooth" });
    isFirstScrollRef.current = false;
  }, [messages]);

  if (messages === undefined) {
    return <ConversationDetailSkeleton />;
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-base text-neutral-400">{t("components.views.shared.message_list.no_messages")}</p>
        {typingIndicator}
      </div>
    );
  }

  const { days, attachments } = groupByDay(messages);

  return (
    <div ref={attachBottomPin} className="flex flex-1 flex-col overflow-y-auto px-4 py-4 md:px-6">
      <div ref={contentRef} className="flex flex-col gap-4">
        <MessageThread
          attachments={attachments}
          currentUserId={currentUserId}
          days={days}
          onToggleReaction={onToggleReaction}
          reactions={reactions}
          receipt={
            readers.length > 0
              ? t("components.views.shared.message_list.read_by", { names: readers.join(", ") })
              : t("components.views.shared.message_list.read")
          }
          receiptRunId={readRunId(days, currentUserId, readAt)}
        />
        {typingIndicator}
      </div>
    </div>
  );
}
